/**
 * MagnifierFeature - Smooth screen magnifier using CSS zoom on cloned body
 */
import BaseFeature from './BaseFeature.js';

export default class MagnifierFeature extends BaseFeature {
    constructor(widget, options = {}) {
        super(widget, options);
        this.zoomLevel = options.zoomLevel || 2;
        this.lensSize = options.lensSize || 200;
        this.lens = null;
        this._clone = null;
        this._raf = null;
        this._mouseMoveHandler = null;
        this._mouseLeaveHandler = null;
        this._lastX = 0;
        this._lastY = 0;
    }

    enable() {
        super.enable();
        this._createLens();
        this._cloneBody();
        this._bindMouse();
    }

    disable() {
        super.disable();
        this._unbindMouse();
        this._removeLens();
        this._removeClone();
    }

    /**
     * Create magnifier lens element
     */
    _createLens() {
        this.lens = document.createElement('div');
        this.lens.className = 'dhA11y-magnifier-lens';
        this.lens.style.cssText = `
            position: fixed;
            width: ${this.lensSize}px;
            height: ${this.lensSize}px;
            border-radius: 50%;
            border: 3px solid #555;
            box-shadow: 0 4px 16px rgba(0,0,0,0.4), inset 0 0 20px rgba(0,0,0,0.05);
            pointer-events: none;
            z-index: 999995;
            overflow: hidden;
            display: none;
            will-change: transform;
        `;
        document.body.appendChild(this.lens);
    }

    /**
     * Clone the body into the lens for smooth zooming
     */
    _cloneBody() {
        this._clone = document.createElement('div');
        this._clone.className = 'dhA11y-magnifier-clone';
        this._clone.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: ${document.documentElement.scrollWidth}px;
            height: ${document.documentElement.scrollHeight}px;
            transform: scale(${this.zoomLevel});
            transform-origin: 0 0;
            pointer-events: none;
            will-change: transform;
        `;

        // Clone the entire document body content
        const bodyClone = document.body.cloneNode(true);
        // Remove the widget itself and magnifier from clone
        const widgetEl = bodyClone.querySelector('#accessibility-widget');
        if (widgetEl) widgetEl.remove();
        const lensEl = bodyClone.querySelector('.dhA11y-magnifier-lens');
        if (lensEl) lensEl.remove();
        const cloneEl = bodyClone.querySelector('.dhA11y-magnifier-clone');
        if (cloneEl) cloneEl.remove();

        this._clone.appendChild(bodyClone);
        this.lens.appendChild(this._clone);
    }

    /**
     * Remove magnifier lens
     */
    _removeLens() {
        if (this.lens) {
            this.lens.remove();
            this.lens = null;
        }
    }

    /**
     * Remove clone
     */
    _removeClone() {
        this._clone = null;
    }

    /**
     * Bind mouse move for magnifier
     */
    _bindMouse() {
        this._mouseMoveHandler = (e) => {
            // Ignore widget UI and control bar
            if (e && e.target && typeof e.target.closest === 'function') {
                if (e.target.closest('#accessibility-widget') || e.target.closest('.accessibility-control-bar')) {
                    if (this.lens) this.lens.style.display = 'none';
                    return;
                }
            }
            this._lastX = e.clientX;
            this._lastY = e.clientY;
            if (!this._raf) {
                this._raf = requestAnimationFrame(() => {
                    this._updateLens(this._lastX, this._lastY);
                    this._raf = null;
                });
            }
        };

        this._mouseLeaveHandler = () => {
            if (this.lens) this.lens.style.display = 'none';
        };

        document.addEventListener('mousemove', this._mouseMoveHandler);
        document.addEventListener('mouseleave', this._mouseLeaveHandler);
    }

    /**
     * Update lens position and content offset
     */
    _updateLens(x, y) {
        if (!this.lens || !this._clone) return;
        this.lens.style.display = 'block';

        const half = this.lensSize / 2;
        const zoom = this.zoomLevel;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        // Position lens centered on cursor
        this.lens.style.left = (x - half) + 'px';
        this.lens.style.top = (y - half) + 'px';

        // Offset the clone so the zoomed area under cursor is centered in lens
        const offsetX = -((x + scrollX) * zoom - half);
        const offsetY = -((y + scrollY) * zoom - half);
        this._clone.style.transform = `scale(${zoom}) translate(${offsetX / zoom}px, ${offsetY / zoom}px)`;
    }

    /**
     * Unbind mouse events
     */
    _unbindMouse() {
        if (this._mouseMoveHandler) {
            document.removeEventListener('mousemove', this._mouseMoveHandler);
            this._mouseMoveHandler = null;
        }
        if (this._mouseLeaveHandler) {
            document.removeEventListener('mouseleave', this._mouseLeaveHandler);
            this._mouseLeaveHandler = null;
        }
        if (this._raf) {
            cancelAnimationFrame(this._raf);
            this._raf = null;
        }
    }
}
